/**
 * ResolvedConfigContext — Prompt 14D-P2.
 *
 * Fetches /api/config/resolved for the current authenticated scope.
 * Refreshes whenever authentication identity or the active
 * restaurantId changes. Stale in-flight responses cannot overwrite
 * the current scope's config (§8 stale-response protection).
 *
 * Contract (matches /app/memory/MISEONE_CONFIGURATION_ARCHITECTURE.md):
 *  {
 *    features: { ai_delivery_reader, barcode_scanner, invoice_module,
 *                waste_report, hot_holding_checks },
 *    modules:  { workforce?, ... },
 *    resolved_from: { "features.xxx": "platform"|"company"|"restaurant" }
 *  }
 *
 * Fail-closed rule: on network/auth failure we return the safe
 * fallback with `modules = {}` — sidebar visibility never opens
 * optimistically before the backend confirms the module (§9).
 */
import { createContext, useCallback, useContext, useEffect,
         useMemo, useRef, useState } from "react";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";

const PLATFORM_FALLBACK = Object.freeze({
    features: {
        ai_delivery_reader: true,
        barcode_scanner:    true,
        invoice_module:     true,
        waste_report:       true,
        hot_holding_checks: true,
    },
    modules: {},
    resolved_from: {},
});

const ResolvedConfigContext = createContext({
    config: PLATFORM_FALLBACK,
    loading: false,
    refresh: () => {},
    isFeatureEnabled: () => true,
});

export function ResolvedConfigProvider({ children }) {
    const { user, restaurantId } = useApp();
    const [config, setConfig] = useState(PLATFORM_FALLBACK);
    const [loading, setLoading] = useState(false);
    // §8 stale-response protection — monotonic request counter. Only
    // the most-recent request's response is allowed to publish.
    const reqIdRef = useRef(0);

    const refresh = useCallback(async () => {
        // §9 fail-closed while unauthenticated: never let a stale
        // login's config bleed into a new session.
        if (!user || user === false) {
            setConfig(PLATFORM_FALLBACK);
            setLoading(false);
            return;
        }
        const myReq = ++reqIdRef.current;
        setLoading(true);
        try {
            const params = restaurantId
                ? { restaurant_id: restaurantId } : undefined;
            const { data } = await api.get("/config/resolved", { params });
            if (myReq !== reqIdRef.current) return; // stale
            setConfig({
                features: { ...PLATFORM_FALLBACK.features, ...(data.features || {}) },
                modules: data.modules || {},
                resolved_from: data.resolved_from || {},
            });
        } catch (_err) {
            if (myReq !== reqIdRef.current) return; // stale
            // §9 · fail closed on error — modules stays empty so
            // module-gated UI (e.g. Workforce sidebar) remains hidden.
            setConfig(PLATFORM_FALLBACK);
        } finally {
            if (myReq === reqIdRef.current) setLoading(false);
        }
    }, [user, restaurantId]);

    // §7 refresh triggers: authentication identity change AND
    // active restaurant change both re-fetch resolved config.
    useEffect(() => { refresh(); }, [refresh]);

    const isFeatureEnabled = useCallback(
        (name) => Boolean(config?.features?.[name] ?? true),
        [config],
    );

    const value = useMemo(
        () => ({ config, loading, refresh, isFeatureEnabled }),
        [config, loading, refresh, isFeatureEnabled],
    );

    return (
        <ResolvedConfigContext.Provider value={value}>
            {children}
        </ResolvedConfigContext.Provider>
    );
}

export function useResolvedConfig() {
    return useContext(ResolvedConfigContext);
}
